import { RuleTemplateData } from '@web-config-app/core';

/**
 * `incomplete` can mean nullish input rules, unresolved placeholders, or a
 * serialization crash. Callers should not treat it as a resolved rule.
 */
export type ReplacePlaceholderInRulesResult =
  | { status: 'complete'; value: any }
  | { status: 'incomplete'; missingKeys: string[] };

// Placeholders are identifier-shaped keys inside braces, e.g. `{campaignId}`.
// Restricting to identifiers avoids matching the JSON object braces around them.
const PLACEHOLDER_PATTERN = /\{([A-Za-z_$][A-Za-z0-9_$]*)\}/g;

export const replacePlaceholderInRules = (
  rules: Record<string, any> | string | null | undefined,
  ruleTemplateData: RuleTemplateData | null | undefined,
  isObject?: boolean,
): ReplacePlaceholderInRulesResult => {
  if (rules === null || rules === undefined) {
    return { status: 'incomplete', missingKeys: [] };
  }

  const templateData = ruleTemplateData ?? {};

  try {
    let ruleString = JSON.stringify(rules);

    const referencedKeys = new Set<string>(
      Array.from(ruleString.matchAll(PLACEHOLDER_PATTERN), (match) => match[1]),
    );

    const missingKeys: string[] = [];
    referencedKeys.forEach((key) => {
      const value = templateData[key];
      if (value === null || value === undefined) {
        missingKeys.push(key);
      }
    });

    if (missingKeys.length > 0) {
      return { status: 'incomplete', missingKeys };
    }

    referencedKeys.forEach((key) => {
      const value = templateData[key];
      const placeholder = `"{${key}}"`;
      const embeddedInStringPlaceholder = `{${key}}`;

      let filteredValue = value;
      // if the value is an array, filter out null or undefined
      if (Array.isArray(value)) {
        filteredValue = value.filter(Boolean);
      }
      const replacementValue = isObject
        ? JSON.parse(JSON.stringify(filteredValue))
        : JSON.stringify(filteredValue);

      ruleString = ruleString
        .replaceAll(placeholder, replacementValue) // replace placeholder with quotes first
        .replaceAll(embeddedInStringPlaceholder, filteredValue.toString());
    });

    return { status: 'complete', value: JSON.parse(ruleString) };
  } catch (error) {
    console.error('Error processing template json:', error);
    return { status: 'incomplete', missingKeys: [] };
  }
};
